import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth.jsx";
import { useI18n } from "../lib/i18n.jsx";
import { ArrowLeft, Edit3, Save, X, BookOpen } from "lucide-react";
import { toast } from "sonner";
import MarkdownContent from "../components/MarkdownContent.jsx";

export default function LectureDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t, pick } = useI18n();
  const [lecture, setLecture] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/lectures/${id}`);
        setLecture(data);
        setDraft({
          title_en: data.title_en, title_nl: data.title_nl,
          content_en: data.content_en, content_nl: data.content_nl,
        });
      } catch (e) {
        if (e.response?.status === 403) setForbidden(true);
        else toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/lectures/${id}`, draft);
      setLecture(data);
      setEditing(false);
      toast.success(t("lectures.saved"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <main className="max-w-4xl mx-auto px-6 md:px-10 py-16"><p className="text-[#7A7061] italic">{t("rituals.unsealing")}</p></main>;
  if (forbidden) {
    return (
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-16" data-testid="lecture-forbidden">
        <Link to="/lectures" className="btn-ghost inline-flex items-center gap-2 mb-8"><ArrowLeft size={14} /> {t("common.back")}</Link>
        <div className="border border-[#C1B49A] p-12 text-center">
          <h2 className="font-display text-3xl italic text-[#8B3A3A] mb-3">{t("lectures.forbidden_title")}</h2>
          <p className="text-[#4A4238]">{t("lectures.forbidden_body")}</p>
        </div>
      </main>
    );
  }
  if (!lecture) return null;

  const isAdmin = user?.role === "admin";
  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <main data-testid="lecture-detail-page" className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <Link to="/lectures" className="btn-ghost inline-flex items-center gap-2 mb-8" data-testid="lecture-back-link">
        <ArrowLeft size={14} /> {t("lectures.back_link")}
      </Link>

      <article className="border border-[#C1B49A] bg-[#EAE0CA]/40 p-8 md:p-12">
        <header className="mb-8 pb-8 etched-divider">
          <p className="label-small text-[#8B3A3A] mb-3 flex items-center gap-2"><BookOpen size={12} /> {t("lectures.grade_label")} · {lecture.grade}</p>
          {!editing ? (
            <h1 className="font-display text-4xl sm:text-5xl text-[#1A1918] tracking-tight leading-tight" data-testid="lecture-title">{pick(lecture, "title")}</h1>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="label-small block mb-2">{t("lectures.edit_title_en")}</label>
                <input data-testid="lecture-edit-title-en" className="input-etched font-display text-2xl" value={draft.title_en || ""} onChange={(e) => setField("title_en", e.target.value)} />
              </div>
              <div>
                <label className="label-small block mb-2">{t("lectures.edit_title_nl")}</label>
                <input data-testid="lecture-edit-title-nl" className="input-etched font-display text-2xl" value={draft.title_nl || ""} onChange={(e) => setField("title_nl", e.target.value)} />
              </div>
            </div>
          )}
        </header>
        {!editing ? (
          <MarkdownContent testId="lecture-content">{pick(lecture, "content")}</MarkdownContent>
        ) : (
          <div className="space-y-6">
            <div>
              <label className="label-small block mb-2">{t("lectures.edit_content_en")}</label>
              <textarea data-testid="lecture-edit-content-en" rows={12} className="textarea-parchment" value={draft.content_en || ""} onChange={(e) => setField("content_en", e.target.value)} />
              <p className="text-xs italic text-[#7A7061] mt-1">{t("common.markdown_hint")}</p>
            </div>
            <div>
              <label className="label-small block mb-2">{t("lectures.edit_content_nl")}</label>
              <textarea data-testid="lecture-edit-content-nl" rows={12} className="textarea-parchment" value={draft.content_nl || ""} onChange={(e) => setField("content_nl", e.target.value)} />
              <p className="text-xs italic text-[#7A7061] mt-1">{t("common.markdown_hint")}</p>
            </div>
          </div>
        )}
        {isAdmin && (
          <div className="mt-10 flex gap-3 justify-end">
            {!editing ? (
              <button data-testid="lecture-edit-btn" onClick={() => setEditing(true)} className="btn-secondary inline-flex items-center gap-2"><Edit3 size={14} /> {t("common.edit")}</button>
            ) : (
              <>
                <button data-testid="lecture-save-btn" onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2"><Save size={14} /> {saving ? t("common.saving") : t("common.save")}</button>
                <button data-testid="lecture-cancel-btn" onClick={() => { setEditing(false); setDraft({ title_en: lecture.title_en, title_nl: lecture.title_nl, content_en: lecture.content_en, content_nl: lecture.content_nl }); }} className="btn-secondary inline-flex items-center gap-2"><X size={14} /> {t("common.cancel")}</button>
              </>
            )}
          </div>
        )}
      </article>
    </main>
  );
}
